import { Dialog } from "primereact/dialog";
import { useCentersFromRequirements } from "../../hooks/queries";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { useContext, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { changeCaseMedicalCenter } from "../../services/medicalCases";
import { ToastContext } from "../../ToastContext";

/**
 * @typedef CaseData
 * @property {number} caseId
 * @property {string} medicalCenterName
 * @property {number[]} requirements
 * @property {number} clientId
 */

/**
 * Modal para cambiar proveedor de un caso en por suscribir
 * @author gabrielm
 * @param {object} props
 * @param {boolean} props.visible
 * @param {function} props.onClose
 * @param {CaseData?} props.caseData
 */
export default function ChangeMedicalCenterDialog({
  visible,
  onClose,
  caseData,
}) {
  const [selectedCenter, setSelectedCenter] = useState(null);

  const availableCenters = useCentersFromRequirements({
    requirements: caseData?.requirements,
    client: caseData?.clientId,
    enabled: !!caseData?.clientId && !!caseData,
  });

  const closeDialog = () => {
    setSelectedCenter(null);
    onClose();
  };

  const toast = useContext(ToastContext);

  const queryClient = useQueryClient();

  const changeMedicalCenterMutation = useMutation({
    mutationFn: changeCaseMedicalCenter,
    onSuccess: ({ type, message }) => {
      if (type === "success") {
        toast.current.show({
          severity: "success",
          summary: "Éxito",
          detail: message,
        });
        closeDialog();
        queryClient.invalidateQueries({ queryKey: ["medicalCasesSubscribe"] });
        queryClient.invalidateQueries({
          queryKey: ["medicalCasesSubscribeAll"],
        });
        queryClient.invalidateQueries({
          queryKey: ["medicalCaseStatusHistory", caseData.caseId],
        });
      } else {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: message,
        });
      }
    },
  });

  return (
    <Dialog
      header="Cambiar proveedor del caso"
      visible={visible}
      onHide={closeDialog}
      draggable={false}
      className="md:w-5"
    >
      <div>
        <p>
          Seleccione el nuevo proveedor para el caso <b>#{caseData?.caseId}</b>
        </p>
        <p>
          Este proveedor reemplazará a <b>{caseData?.medicalCenterName}</b>
        </p>
        <Dropdown
          value={selectedCenter}
          onChange={(e) => setSelectedCenter(e.value)}
          options={
            availableCenters.data?.filter(
              (center) =>
                center.medical_center_name !== caseData?.medicalCenterName
            ) || []
          }
          loading={availableCenters.isLoading}
          disabled={availableCenters.isLoading}
          optionLabel="medical_center_name"
          optionValue="medical_center"
          placeholder="Seleccione un proveedor"
          className="w-full"
          filter
        />
        <div className="flex mt-3 justify-content-between gap-3">
          <Button
            className="w-full"
            severity="secondary"
            label="Cancelar"
            onClick={closeDialog}
            disabled={changeMedicalCenterMutation.isPending}
          />
          <Button
            className="w-full"
            label="Confirmar"
            disabled={
              selectedCenter === null || changeMedicalCenterMutation.isPending
            }
            loading={changeMedicalCenterMutation.isPending}
            onClick={() => {
              changeMedicalCenterMutation.mutate({
                caseId: caseData.caseId,
                medicalCenterId: selectedCenter,
              });
            }}
          />
        </div>
      </div>
    </Dialog>
  );
}
